'use client';

import { useResourceGroups, useRoadmapDefaultResourceAllocations } from '@/domain/resources/hooks';
import { type RoadmapDefaultResourceAllocationDTO, type RoadmapResource } from '@/domain/resources/types';
import { useRoadmapEditStore } from '@/features/roadmap/store/roadmap';
import { largestRemainderIntegers } from '@/features/roadmap/utils/planningHours';
import { type ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';

export interface ActivityRow {
  id: number;
  resourceName: string;
  alias: string;
  hours: number;
  deletedAt?: string | null;
}

export interface UsePlannedHoursSectionProps {
  createRoadmapMode?: boolean;
  isClone?: boolean;
}

function redistributeByDefaultAllocations(
  rows: ActivityRow[],
  plannedHours: number,
  defaultAllocations: RoadmapDefaultResourceAllocationDTO[],
): ActivityRow[] {
  if (rows.length === 0) return [];
  if (plannedHours === 0) return rows.map((r) => ({ ...r, hours: 0 }));
  const allocationMap = new Map(defaultAllocations.map((d) => [d.roadmapResourceId, d.percentage]));
  const rawValues = rows.map((r) => ((allocationMap.get(r.id) ?? 0) / 100) * plannedHours);
  const distributed = largestRemainderIntegers(rawValues);
  return rows.map((r, i) => ({ ...r, hours: distributed[i] }));
}

function buildRowsFromSavedResources(
  allResources: {
    idRoadmapResource: number;
    resource: string;
    alias: string;
    hours: number;
    deletedAt?: string | null;
  }[],
): ActivityRow[] {
  const grouped = allResources.reduce<
    Record<number, { resourceName: string; alias: string; hours: number; deletedAt?: string | null }>
  >((acc, r) => {
    if (!acc[r.idRoadmapResource]) {
      acc[r.idRoadmapResource] = { resourceName: r.resource, alias: r.alias, hours: 0, deletedAt: r.deletedAt ?? null };
    }
    acc[r.idRoadmapResource].hours += r.hours;
    return acc;
  }, {});

  return Object.entries(grouped).map(([id, g]) => ({
    id: Number(id),
    resourceName: g.resourceName,
    alias: g.alias,
    hours: g.hours,
    deletedAt: g.deletedAt,
  }));
}

export function usePlannedHoursSection({
  createRoadmapMode = false,
  isClone = false,
}: Readonly<UsePlannedHoursSectionProps>) {
  const storeHours = useRoadmapEditStore((s) => s.editableRoadMap.plannedHours ?? 0);
  const idRoadmap = useRoadmapEditStore((s) => s.editableRoadMap.idRoadmap);
  const versionYear = useRoadmapEditStore((s) => {
    const version = s.editableRoadMap.version;
    return version ? new Date(version.releaseDate).getFullYear() : new Date().getFullYear();
  });

  const { data: resources = [] } = useResourceGroups(versionYear);

  const { data: defaultAllocations = [] } = useRoadmapDefaultResourceAllocations();
  const defaultAllocationsRef = useRef(defaultAllocations);
  useEffect(() => {
    defaultAllocationsRef.current = defaultAllocations;
  }, [defaultAllocations]);

  const [inputValue, setInputValue] = useState(() => String(storeHours));
  const [rows, setRows] = useState<ActivityRow[]>([]);
  const [pendingResources, setPendingResources] = useState<RoadmapResource[]>([]);

  const savedActivitiesKey = useRoadmapEditStore((s) =>
    (s.editableRoadMap.type?.roadmapStatus ?? [])
      .flatMap((st) => st.resources ?? [])
      .map((r) => r.idRoadmapStatusResource)
      .join(','),
  );

  useEffect(() => {
    setInputValue(String(storeHours));
  }, [storeHours]);

  useEffect(() => {
    if (!createRoadmapMode || defaultAllocations.length === 0 || resources.length === 0) return;
    if (isClone) return;

    const resourceMap = new Map(resources.map((r) => [r.idRoadmapResource, r]));
    const initialRows: ActivityRow[] = defaultAllocations
      .map((d) => {
        const res = resourceMap.get(d.roadmapResourceId);
        if (!res) return null;
        return { id: res.idRoadmapResource, resourceName: res.resource, alias: res.alias, hours: 0 };
      })
      .filter((r): r is ActivityRow => r !== null);

    if (initialRows.length === 0) return;

    const currentHours = useRoadmapEditStore.getState().editableRoadMap.plannedHours ?? 0;
    setRows(redistributeByDefaultAllocations(initialRows, currentHours, defaultAllocations));
  }, [createRoadmapMode, isClone, defaultAllocations, resources]);

  useEffect(() => {
    if (createRoadmapMode) return;
    setRows([]);
    if (!idRoadmap) return;

    const { editableRoadMap: current } = useRoadmapEditStore.getState();
    const activityResources = (current.type?.roadmapStatus ?? [])
      .flatMap((st) => st.resources ?? [])
      .filter((r) => r.idResource === null);
    if (activityResources.length === 0) return;

    const built = buildRowsFromSavedResources(activityResources);
    const totalSavedHours = built.reduce((sum, r) => sum + r.hours, 0);
    if (totalSavedHours === 0) return;

    setRows(built);
  }, [createRoadmapMode, idRoadmap, savedActivitiesKey]);

  useEffect(() => {
    useRoadmapEditStore.setState({
      plannedHoursRows: rows.map(({ id, hours, resourceName, alias }) => ({
        id,
        hours,
        resource: resourceName,
        alias,
      })),
    });
  }, [rows]);

  const plannedHours = Number.parseFloat(inputValue) || 0;
  const plannedHoursRef = useRef(plannedHours);
  useEffect(() => {
    plannedHoursRef.current = plannedHours;
  }, [plannedHours]);

  const totalHours = rows.reduce((sum, r) => sum + r.hours, 0);

  const handleInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setInputValue(value);
      const parsed = Math.max(0, Number.parseFloat(value) || 0);
      useRoadmapEditStore.setState((state) => ({
        editableRoadMap: { ...state.editableRoadMap, plannedHours: parsed },
        isPlannedHoursDirty: true,
      }));
      if (createRoadmapMode) {
        setRows((prev) => {
          if (prev.length === 0) return prev;
          return redistributeByDefaultAllocations(prev, parsed, defaultAllocationsRef.current);
        });
      }
    },
    [createRoadmapMode],
  );

  const commitToStore = useCallback(() => {
    const rounded = Math.max(0, Math.round(plannedHoursRef.current));
    setInputValue(String(rounded));
    const prevPlanned = useRoadmapEditStore.getState().editableRoadMap.plannedHours ?? 0;
    useRoadmapEditStore.setState((state) => ({
      editableRoadMap: { ...state.editableRoadMap, plannedHours: rounded },
      isStoreDirty: prevPlanned !== rounded ? true : state.isStoreDirty,
      isPlannedHoursDirty: prevPlanned !== rounded ? true : state.isPlannedHoursDirty,
    }));
    if (createRoadmapMode) {
      setRows((prev) => {
        return redistributeByDefaultAllocations(prev, rounded, defaultAllocationsRef.current);
      });
    }
  }, [createRoadmapMode]);

  const handleAdd = useCallback((newResources: RoadmapResource[]) => {
    setRows((prev) => {
      const newItems = newResources.map((r) => ({
        id: r.idRoadmapResource,
        resourceName: r.resource,
        alias: r.alias,
        hours: 0,
      }));
      const allRows = prev.length === 0 ? newItems : [...prev, ...newItems];
      return allRows;
    });
    useRoadmapEditStore.setState({ isStoreDirty: true, isPlannedHoursDirty: true });
  }, []);

  const missingDefaultIds = useMemo(() => {
    const currentIds = new Set(rows.map((r) => r.id));
    return defaultAllocations.map((d) => d.roadmapResourceId).filter((id) => !currentIds.has(id));
  }, [rows, defaultAllocations]);

  const showAssignDefaults = defaultAllocations.length > 0;
  const canAssignDefaults = missingDefaultIds.length > 0;

  const handleAssignDefaults = useCallback(() => {
    setRows((prev) => {
      const resourceMap = new Map(resources.map((r) => [r.idRoadmapResource, r]));
      const currentIds = new Set(prev.map((r) => r.id));
      const newItems: ActivityRow[] = defaultAllocations
        .filter((d) => !currentIds.has(d.roadmapResourceId))
        .map((d) => {
          const res = resourceMap.get(d.roadmapResourceId);
          if (!res) return null;
          return { id: res.idRoadmapResource, resourceName: res.resource, alias: res.alias, hours: 0 };
        })
        .filter((r): r is ActivityRow => r !== null);

      const allRows = [...prev, ...newItems];
      const currentHours = useRoadmapEditStore.getState().editableRoadMap.plannedHours ?? 0;
      return redistributeByDefaultAllocations(allRows, currentHours, defaultAllocations);
    });
    useRoadmapEditStore.setState({ isStoreDirty: true, isPlannedHoursDirty: true });
  }, [resources, defaultAllocations]);

  const handleDelete = useCallback((id: number) => {
    setRows((prev) => {
      const remaining = prev.filter((r) => r.id !== id);
      return remaining;
    });
    useRoadmapEditStore.setState({ isStoreDirty: true, isPlannedHoursDirty: true });
  }, []);

  const handleHoursChange = useCallback((id: number, value: string) => {
    const h = Math.max(0, Math.round(Number.isNaN(Number.parseFloat(value)) ? 0 : Number.parseFloat(value)));
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, hours: h } : r)));
    useRoadmapEditStore.setState({ isStoreDirty: true, isPlannedHoursDirty: true });
  }, []);

  const rowIdKey = useMemo(
    () =>
      rows
        .map((r) => r.id)
        .sort((a, b) => a - b)
        .join(','),
    [rows],
  );
  const alreadyAdded = useMemo(() => new Set(rowIdKey ? rowIdKey.split(',').map(Number) : []), [rowIdKey]);

  return {
    rows,
    inputValue,
    plannedHours,
    totalHours,
    pendingResources,
    setPendingResources,
    resources,
    alreadyAdded,
    handleInputChange,
    commitToStore,
    handleAdd,
    handleDelete,
    handleHoursChange,
    showAssignDefaults,
    canAssignDefaults,
    handleAssignDefaults,
  };
}
